import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Star, Lock, CheckCircle2 } from "lucide-react";
import { stripePromise } from "@/lib/stripe";
import { api } from "@/lib/api";

const UpdateForm = ({ token, onDone }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError("");
    try {
      const { error: stripeErr, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });
      if (stripeErr) {
        setError(stripeErr.message || "Card could not be saved");
        return;
      }
      await api.post("/card-link/confirm", {
        token,
        payment_method_id: setupIntent.payment_method,
      });
      onDone();
    } catch (err) {
      console.error("card save failed", err);
      setError(
        err.response?.data?.detail ||
        err.message ||
        "Something went wrong — please call us at 480-940-6023 and we'll take your card over the phone."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <PaymentElement />
      {error && (
        <p data-testid="update-card-error" className="mt-3 text-sm font-semibold text-rose-500">{error}</p>
      )}
      <button
        data-testid="update-card-submit"
        type="submit"
        disabled={saving || !stripe}
        className="mt-5 w-full rounded-xl bg-fall-amber px-5 py-3.5 font-display text-sm font-extrabold uppercase tracking-widest text-brand-navy transition-transform hover:scale-[1.02] disabled:opacity-50"
      >
        {saving ? "Saving securely…" : "Save my card"}
      </button>
    </form>
  );
};

export default function UpdateCard() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [fail, setFail] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    api
      .get(`/card-link/${token}`)
      .then((r) => {
        setInfo(r.data);
        return api.post("/card-link/setup-intent", { token });
      })
      .then((r) => setClientSecret(r.data.client_secret))
      .catch((err) => setFail(err.response?.data?.detail || "This link is no longer valid."));
  }, [token]);

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-brand-navy px-4 py-16">
      <div data-testid="update-card-page" className="w-full max-w-md rounded-3xl border border-white/10 bg-white p-7 shadow-2xl">
        <div className="flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-fall-amber">
            <Star className="h-5 w-5 fill-brand-navy text-brand-navy" />
          </span>
          <div>
            <p className="font-display text-lg font-extrabold text-brand-navy">Oxford Cleaners</p>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-fall-orange">Secure card update</p>
          </div>
        </div>

        {fail ? (
          <p data-testid="update-card-invalid" className="mt-6 rounded-xl bg-rose-50 p-4 text-sm font-semibold text-rose-600">
            {fail} Call us at <a className="text-brand-blue" href="tel:4809406023">480-940-6023</a>.
          </p>
        ) : done ? (
          <div data-testid="update-card-success" className="mt-6 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <h1 className="mt-3 font-display text-xl font-extrabold text-brand-navy">Card saved — you're all set!</h1>
            <p className="mt-2 text-sm text-slate-600">
              We'll charge this card when your order is on its way back to you. Thanks, {info?.name}!
            </p>
          </div>
        ) : (
          <>
            <h1 className="mt-6 font-display text-xl font-extrabold text-brand-navy">
              Hi {info?.name || "there"} — add your card for delivery
            </h1>
            <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
              <Lock className="h-3.5 w-3.5 text-emerald-600" />
              Secured by Stripe — we never see or store your card number
            </p>
            <div className="mt-5">
              {clientSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret, paymentMethodCreation: "manual" }}>
                  <UpdateForm token={token} onDone={() => setDone(true)} />
                </Elements>
              ) : (
                <p className="py-8 text-center text-sm font-semibold text-slate-400">Loading secure form…</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
